import { SortQualifier } from '@core/filters/path-filter/dsl/search-bar';
import { SortableColCfg } from '@core/models/entities/entity-visualization-interface';
import { isNil } from '@core/utils/shared-utils';

import { SortState } from '@shared/components/table/table.model';

/** A label column sorts by its `labelKey`, which a Path sort cannot name: callers leave those out. */
export function qualifierForColumn(
  col: SortableColCfg,
  direction: 'asc' | 'desc'
): SortQualifier {
  return { field: col.dbColName, order: direction };
}

/** A table sort as Path sorts; columns a Path sort cannot name are left out. */
export function qualifiersForSortState<T>(
  sortState: SortState<T>[],
  sortableColMap: Record<string, SortableColCfg>
): SortQualifier[] {
  return sortState.flatMap(({ column, direction }) => {
    const col = sortableColMap[String(column)];
    return col && isNil(col.labelKey) ? [qualifierForColumn(col, direction)] : [];
  });
}

/** The header state the table shows for the qualifiers that name a column. */
export function sortStateFor<T>(
  sort: SortQualifier[],
  sortableColMap: Record<string, SortableColCfg>
): SortState<T>[] {
  const columns = columnKeysByField(sortableColMap);
  return sort.flatMap(({ field, order, association }) => {
    const column = association ? undefined : columns.get(field);
    return isNil(column) ? [] : [{ column: column as keyof T, direction: order }];
  });
}

/**
 * The Sort tab's list once the table's header sort became `header`. A column entry the header
 * still holds keeps its place and takes the header's direction, one it dropped leaves, a column
 * new to the list goes last. Entries no column shows stay where they are.
 */
export function withHeaderSort<T>(
  sort: SortQualifier[],
  header: SortState<T>[],
  sortableColMap: Record<string, SortableColCfg>
): SortQualifier[] {
  const columns = columnKeysByField(sortableColMap);
  const fromHeader = new Map(
    qualifiersForSortState(header, sortableColMap).map((qualifier) => [
      qualifier.field,
      qualifier
    ])
  );
  const kept = sort.flatMap((qualifier) => {
    if (qualifier.association || !columns.has(qualifier.field)) return [qualifier];
    const edited = fromHeader.get(qualifier.field);
    fromHeader.delete(qualifier.field);
    return edited ? [edited] : [];
  });
  return [...kept, ...fromHeader.values()];
}

/** The first column sorting on each field; label columns are not Path sorts. */
function columnKeysByField(
  sortableColMap: Record<string, SortableColCfg>
): Map<string, string> {
  const columns = new Map<string, string>();
  for (const [key, col] of Object.entries(sortableColMap)) {
    if (isNil(col.labelKey) && !columns.has(col.dbColName)) columns.set(col.dbColName, key);
  }
  return columns;
}
